import type {
  JobsApi,
  JobsAiSummaryResult,
  CareerSearchProfile,
  JobsListParams,
  JobsListResponse,
  TargetedRefreshResponse,
  TrackJobInput,
  TrackedJob,
  TrackerApi,
  TrackerListQuery,
  TrackerListResponse,
  UpdateTrackedJobInput,
} from "@/features/job-tracker/contracts";
import { httpClient } from "@/shared/api/httpClient";

function dateOnlyToIso(value: string | null | undefined): string | null | undefined {
  if (value === null || value === undefined) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(value).toISOString();
  }
  return value;
}

function trackerQs(query?: TrackerListQuery): string {
  const params = new URLSearchParams();
  if (query?.status) params.set("status", query.status);
  params.set("limit", String(query?.limit ?? 200));
  if (query?.offset !== undefined) params.set("offset", String(query.offset));
  return `?${params.toString()}`;
}

function jobsQs(params?: JobsListParams): string {
  const sp = new URLSearchParams();
  if (params?.page !== undefined) sp.set("page", String(params.page));
  if (params?.pageSize !== undefined) sp.set("pageSize", String(params.pageSize));
  if (params?.company?.trim()) sp.set("company", params.company.trim());
  if (params?.location?.trim()) sp.set("location", params.location.trim());
  if (params?.q?.trim()) sp.set("q", params.q.trim());
  if (params?.active !== undefined) sp.set("active", String(params.active));
  if (params?.sort) sp.set("sort", params.sort);
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export const realTrackerApi: TrackerApi = {
  async list(query?: TrackerListQuery): Promise<TrackerListResponse> {
    return httpClient.get<TrackerListResponse>(`/api/tracker${trackerQs(query)}`);
  },

  async track(input: TrackJobInput): Promise<TrackedJob> {
    return httpClient.post<TrackedJob>(
      "/api/tracker",
      {
        ...input,
        appliedAt: dateOnlyToIso(input.appliedAt),
      } as unknown as Record<string, unknown>,
    );
  },

  async update(id: string, input: UpdateTrackedJobInput): Promise<TrackedJob> {
    return httpClient.patch<TrackedJob>(
      `/api/tracker/${encodeURIComponent(id)}`,
      {
        ...input,
        appliedAt: input.appliedAt === undefined ? undefined : dateOnlyToIso(input.appliedAt),
      } as unknown as Record<string, unknown>,
    );
  },

  // 204 No Content per guide §5. httpClient already returns undefined for empty bodies.
  async untrack(id: string): Promise<void> {
    await httpClient.del<void>(`/api/tracker/${encodeURIComponent(id)}`);
  },
};

export const realJobsApi: JobsApi = {
  async list(params?: JobsListParams): Promise<JobsListResponse> {
    return httpClient.get<JobsListResponse>(`/api/jobs${jobsQs(params)}`);
  },

  async getAiSummary(): Promise<JobsAiSummaryResult> {
    return httpClient.post<JobsAiSummaryResult>("/api/jobs/ai-summary", {});
  },

  async getSearchProfile(): Promise<CareerSearchProfile> {
    return httpClient.get<CareerSearchProfile>("/api/jobs/search-profile");
  },

  async rebuildSearchProfile(): Promise<CareerSearchProfile> {
    return httpClient.post<CareerSearchProfile>("/api/jobs/search-profile/rebuild", {});
  },

  async startTargetedRefresh(): Promise<TargetedRefreshResponse> {
    return httpClient.post<TargetedRefreshResponse>("/api/job-discovery/targeted-refresh", {});
  },

  async getTargetedRefreshStatus(refreshId: string): Promise<TargetedRefreshResponse> {
    return httpClient.get<TargetedRefreshResponse>(
      `/api/job-discovery/targeted-refresh/${encodeURIComponent(refreshId)}`,
    );
  },
};
